/**
 * The AI board — the Azure AI estate as it actually measures today: what is
 * being throttled, what is nearly out of quota, what is provisioned and idle,
 * what nothing is watching, and what the estate is made of.
 *
 * ## Unlike the boards added around it, this one is live on first paint
 *
 * The managed identity already holds `Reader` at the tenant root, so the state
 * this page is normally seen in is `access_state: "ok"` with real numbers. There
 * is no ships-dark setup panel to design around. What the numbers say is not
 * flattering, which is the point: on 2026-08-20 one account refused 33.74% of
 * its calls (3,712 of 11,003), one quota sat at 89.55% of a `DataZoneStandard`
 * limit, three accounts had no deployment at all, three served no tokens, and
 * **not one AI resource in the estate had an alert rule or a diagnostic
 * setting**.
 *
 * `access_state` is still handled exhaustively, because a 403 arriving as `0 AI
 * accounts, 0 exhausted quotas, 0 throttled calls` would report a smaller,
 * cheaper, better-governed estate than the real one and give no hint it was
 * doing so. That branch lives in `./page-frame.tsx` and `./access-panel.tsx`:
 * when a payload is not `ok`, its panels do not render at all — no strip of
 * zeros, no empty axis, no flat line along the bottom.
 *
 * ## Altitudes (§1)
 *
 * - **L0** — `./kpi-strip.tsx`: six numbers, **actionable first**. The HTTP 429
 *   rate and the worst quota utilisation lead; the census follows; the two
 *   coverage tiles come last and have inverted polarity, so a big number there
 *   is bad news and is toned accordingly.
 * - **L1** — `./findings-panel.tsx` (what to look at, worst first, ranked by the
 *   backend from thresholds so a newly exhausted quota surfaces with no code
 *   change), then `./attention-section.tsx` (throttling, quota headroom,
 *   observability gaps — worst-N only), then `./composition-section.tsx` (what
 *   the estate is made of, discovered by resource type).
 * - **L2** — each chart's **table twin** (`?data=<id>`, in the URL, back-button
 *   closable). The per-account and per-quota full lists belong to `/ai/estate`
 *   and `/ai/capacity`, which have no page yet — see below.
 *
 * ## Why one page and not the five `./params.ts` declares
 *
 * `params.ts` declares five `/ai/*` routes; only this one has a page component
 * today, so `AI_BUILT_PATHS` lists exactly one path, the sub-nav does not render
 * a row of links that 404, and every KPI tile whose route is unbuilt lands on the
 * section carrying the same content here (`aiHref`). Adding `/ai/capacity` later
 * is one string in `AI_BUILT_PATHS`; no call site changes.
 *
 * ## Three queries, one backend snapshot
 *
 * `/ai/overview`, `/ai/estate` and `/ai/capacity` are projections of one cached
 * build (`./hooks` reads each payload's own `cache_ttl_sec` for the poll
 * interval), so asking for all three costs one Azure read and guarantees the KPI
 * strip and the panels below it cannot disagree. Capacity is not optional: the
 * **thresholds** every tone on this page is judged against — the throttle
 * warning rate and the quota warning line — are payload fields, not constants
 * here, so they cannot drift from the backend's own env-tunable values.
 *
 * Nothing on this board is persisted: `@/lib/api/persist.ts` is a
 * deny-by-default allow-list and `["ai", …]` is deliberately absent from it.
 * These payloads name internal accounts, model deployments and what the estate
 * spends.
 */

import { Alert, Skeleton } from "@norican/react";

import { useAiCapacity, useAiEstate, useAiOverview } from "@/lib/api/hooks/ai";

import { AiAccessPanel, AiCoverageAlert } from "./access-panel";
import { AiAttentionSection } from "./attention-section";
import { AiCompositionSection } from "./composition-section";
import { AiFindingsPanel } from "./findings-panel";
import { AiKpiStrip } from "./kpi-strip";
import { AiPageFrame } from "./page-frame";
import { AI_OVERVIEW_PATH, FINDINGS_ANCHOR } from "./params";
import { isPanelOk, staleAfterMs } from "./status";

export { AI_OVERVIEW_PATH } from "./params";

export function AiPage() {
  const overview = useAiOverview();
  // Same cached snapshot, projected differently: the per-account rates and
  // coverage live on the estate payload, the quota rows and every threshold on
  // the capacity one.
  const estate = useAiEstate();
  const capacity = useAiCapacity();

  const overviewData = overview.data;
  const estateData = estate.data;
  const capacityData = capacity.data;

  const isFetching = overview.isFetching || estate.isFetching || capacity.isFetching;

  return (
    <AiPageFrame
      path={AI_OVERVIEW_PATH}
      panel={overviewData}
      isPending={overview.isPending}
      isFetching={isFetching}
      error={overview.error}
      extraCollectedAt={[estateData?.collected_at, capacityData?.collected_at]}
      extraErrors={[...(estateData?.errors ?? []), ...(capacityData?.errors ?? [])]}
    >
      {overviewData && (
        <>
          {/* ── L0: actionable first, census second, coverage last ───────── */}
          {capacityData ? (
            <AiKpiStrip
              summary={overviewData.summary}
              coverage={overviewData.coverage}
              throttleWarningPct={capacityData.summary.throttle_warning_pct}
              quotaWarningPct={capacityData.summary.warning_utilisation_pct}
              window={overviewData.window}
            />
          ) : capacity.error != null ? (
            // The tiles are *toned* against thresholds that only `/ai/capacity`
            // publishes. Withholding the strip is the honest branch: toning
            // 33.74% against a threshold guessed in the frontend would be a
            // verdict this page has no evidence for.
            <Alert tone="warning" title="The headline tiles are waiting on the capacity route">
              The throttling and quota thresholds every tile is judged against come from
              `/ai/capacity`, which did not answer. The figures below are unaffected — only the
              tiles that would need a threshold to be coloured are withheld, rather than shown
              against a threshold invented here.
            </Alert>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-busy="true">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <Skeleton key={index} height="7rem" />
              ))}
            </div>
          )}

          {/* Coverage is stated on every load, not only when it is a finding:
              every count on this board is a floor until this says otherwise. */}
          <AiCoverageAlert coverage={overviewData.coverage} always />

          {/* ── L1: what to look at, worst first ─────────────────────────── */}
          <AiFindingsPanel findings={overviewData.findings} id={FINDINGS_ANCHOR} />

          {/* ── L1: the panels the leading KPI tiles open ─────────────────── */}
          {estateData &&
            capacityData &&
            (isPanelOk(estateData) && isPanelOk(capacityData) ? (
              <AiAttentionSection
                accounts={estateData.accounts}
                quotas={capacityData.quotas}
                capacity={capacityData.summary}
                window={capacityData.window}
              />
            ) : (
              <AiAccessPanel
                panel="Throttling, quota headroom and observability"
                state={isPanelOk(capacityData) ? estateData : capacityData}
              />
            ))}

          {/* ── L1: what the estate is made of ───────────────────────────── */}
          <AiCompositionSection
            byKind={overviewData.by_kind}
            byRegion={overviewData.by_region}
            byType={overviewData.by_type}
            mcpDiscovered={overviewData.summary.mcp_servers_discovered}
            mcpNotRunning={overviewData.summary.mcp_servers_not_running}
            collectedAt={overviewData.collected_at}
            staleAfterMs={staleAfterMs(overviewData.cache_ttl_sec)}
          />
        </>
      )}
    </AiPageFrame>
  );
}

export { AiClaudePage } from "./claude-page";
export { AiCopilotPage } from "./copilot-page";
